import { useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Upload, Sparkles, X } from 'lucide-react';
import GoldRule from './GoldRule';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const BAG_LIKE_CATEGORIES = new Set(['Business Bags', 'Bags', 'Portfolios']);

// Where a logo naturally sits depends on the piece: on a bag it belongs
// just below where the two handles meet the body (small, modest — see the
// house's own embossed crest on the homepage hero); on a flatter piece
// (wallet, notebook, travel case) it reads better centered on the panel.
const overlayStyleFor = (category) =>
  BAG_LIKE_CATEGORIES.has(category)
    ? { top: '44%', left: '50%', width: '19%', maxHeight: '11%' }
    : { top: '50%', left: '50%', width: '27%', maxHeight: '17%' };

// "See your logo on this set" — instant overlay preview the moment a logo
// is dropped, plus an optional one-click AI-realistic (foil-stamped) render.
// `canvasOptions` is up to two of the set's own pieces — a bag-like piece
// and a flatter piece like a wallet — chosen by the parent page based on
// what's actually in the set.
export default function LogoCustomizer({ canvasOptions = [], onDesignUploaded }) {
  const [selected, setSelected] = useState(0);
  const [file, setFile] = useState(null);
  const [designUrl, setDesignUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode] = useState('quick'); // 'quick' | 'ai'
  const [aiImage, setAiImage] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const canvasProduct = canvasOptions[selected];
  if (!canvasProduct) return null;

  const switchCanvas = (idx) => {
    if (idx === selected) return;
    setSelected(idx);
    setAiImage(null);
    setMode('quick');
  };

  const handleFile = async (f) => {
    if (!f || !f.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    setFile(f);
    setAiImage(null);
    setMode('quick');
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await axios.post(`${API}/customize/upload`, fd);
      setDesignUrl(data.url);
      onDesignUploaded?.(data.url);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Upload failed — please try again.');
    } finally {
      setUploading(false);
    }
  };

  const clear = () => {
    setFile(null);
    setDesignUrl(null);
    setAiImage(null);
    setMode('quick');
    setError(null);
    onDesignUploaded?.(null);
  };

  const generateRealistic = async () => {
    if (!file) return;
    setAiLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('product_code', canvasProduct.code);
      const { data } = await axios.post(`${API}/customize/preview`, fd, { timeout: 45000 });
      setAiImage(data.image);
      setMode('ai');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not generate a preview — please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <section className="hairline-t border-subtle" data-testid="set-logo-customizer">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="eyebrow text-gold mb-5">Make it yours</div>
        <h2 className="font-display text-2xl md:text-3xl text-mb">
          See your logo on the <span className="italic">{canvasProduct.name}.</span>
        </h2>
        <div className="mt-8">
          <GoldRule size="med" />
        </div>

        {canvasOptions.length > 1 && (
          <div className="flex items-center gap-2 mt-8" data-testid="customizer-canvas-toggle">
            {canvasOptions.map((opt, idx) => (
              <button
                key={opt.code}
                type="button"
                onClick={() => switchCanvas(idx)}
                className={`eyebrow-sm px-4 py-2 hairline transition-colors ${
                  idx === selected ? 'border-gold text-gold' : 'border-mb-border text-mb-muted hover:text-mb'
                }`}
                data-testid={`customizer-canvas-option-${idx}`}
              >
                {opt.name}
              </button>
            ))}
          </div>
        )}

        <div className="mt-12 grid md:grid-cols-[1fr_1.1fr] gap-10 items-start">
          {/* Dropzone / controls */}
          <div>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const f = e.dataTransfer.files?.[0];
                if (f) handleFile(f);
              }}
              onClick={() => fileRef.current?.click()}
              className={`hairline border-dashed rounded-sm p-8 min-h-[160px] flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors ${dragOver ? 'border-gold bg-mb-surface-rich/40' : 'border-mb-border'}`}
              data-testid="customizer-dropzone"
            >
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.[0]) handleFile(e.target.files[0]);
                  e.target.value = '';
                }}
                data-testid="customizer-file-input"
              />
              {uploading ? (
                <Loader2 size={22} className="animate-spin text-gold" />
              ) : designUrl ? (
                <>
                  <img
                    src={`${BACKEND}${designUrl}`}
                    alt="Your logo"
                    className="h-16 object-contain"
                    data-testid="customizer-logo-thumb"
                  />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); clear(); }}
                    className="eyebrow-sm text-mb-muted hover:text-gold flex items-center gap-1"
                    data-testid="customizer-clear-btn"
                  >
                    <X size={12} /> Remove &amp; try another
                  </button>
                </>
              ) : (
                <>
                  <Upload size={22} className="text-mb-muted" />
                  <div className="eyebrow-sm text-mb-muted text-center">
                    Drop your company logo here, or click to browse
                  </div>
                </>
              )}
            </div>

            {designUrl && (
              <button
                type="button"
                onClick={generateRealistic}
                disabled={aiLoading}
                className="btn-outline-gold mt-5 disabled:opacity-50"
                data-testid="customizer-ai-preview-btn"
              >
                {aiLoading ? (
                  <><Loader2 size={14} className="animate-spin" /> Rendering a realistic preview…</>
                ) : (
                  <><Sparkles size={14} strokeWidth={1.25} /> Generate a realistic preview</>
                )}
              </button>
            )}

            {error && (
              <p className="text-[13px] text-red-400 mt-4" data-testid="customizer-error">{String(error)}</p>
            )}

            <p className="eyebrow-sm text-mb-muted mt-6 leading-relaxed">
              Just a preview — when you place an order you'll get the chance
              to attach this (or a fresh file) so the atelier can brand it for real.
            </p>
          </div>

          {/* Preview canvas */}
          <div className="relative bg-mb-surface hairline border-subtle aspect-[4/5] overflow-hidden flex items-center justify-center">
            {mode === 'ai' && aiImage ? (
              <img
                src={aiImage}
                alt={`${canvasProduct.name} with your logo — realistic preview`}
                className="w-full h-full object-cover"
                data-testid="customizer-ai-result"
              />
            ) : (
              <>
                <img
                  src={canvasProduct.image}
                  alt={canvasProduct.name}
                  className="w-full h-full object-cover"
                  data-testid="customizer-base-image"
                />
                {designUrl && (
                  <img
                    src={`${BACKEND}${designUrl}`}
                    alt="Your logo, placed on the product"
                    className="absolute object-contain drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)] opacity-90"
                    style={{
                      ...overlayStyleFor(canvasProduct.category),
                      transform: 'translate(-50%, -50%)',
                    }}
                    data-testid="customizer-quick-overlay"
                  />
                )}
              </>
            )}
            {aiLoading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <Loader2 size={28} className="animate-spin text-gold" />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
